import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { api } from '../api';
import { AuthContext } from '../App';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083';

const BPX = 2;
const BC = { y: '#ffd700', Y: '#ffe866', d: '#2a2a2e' };
const BELL_ROWS = [
  '..yy..',
  '.yYYy.',
  '.yYYy.',
  'yYYYYy',
  'yyyyyy',
  '..dd..',
];
function BellSprite() {
  const shadows = [];
  const parsed = BELL_ROWS.map(r => r.split(''));
  for (let ry = 0; ry < parsed.length; ry++)
    for (let rx = 0; rx < parsed[ry].length; rx++) {
      const ch = parsed[ry][rx];
      if (ch !== '.' && BC[ch]) shadows.push(`${rx*BPX}px ${ry*BPX}px 0 ${BC[ch]}`);
    }
  const w = Math.max(...parsed.map(r => r.length));
  return <div style={{ width: w*BPX, height: parsed.length*BPX, position: 'relative', imageRendering: 'pixelated' }}>
    <div style={{ position: 'absolute', width: BPX, height: BPX, boxShadow: shadows.join(',') }} />
  </div>;
}

const TYPE_ICON = { live: '[!]', tip: '[♥]', sub: '[★]', default: '[?]' };

export default function NotificationBell() {
  const { user } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch on mount
  useEffect(() => {
    if (!user) return;
    api.notifications().then(d => {
      setNotifications(d.notifications);
      setUnread(d.unread);
    }).catch(() => {});
  }, [user]);

  // Real-time via SocketIO
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    const socket = io(BASE, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => socket.emit('auth', { token }));
    socket.on('notification', (notif) => {
      setNotifications(prev => [{ ...notif, id: Date.now(), is_read: false, created_at: new Date().toISOString() }, ...prev]);
      setUnread(n => n + 1);
    });
    return () => socket.disconnect();
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open && unread > 0) {
      api.markRead().then(() => setUnread(0)).catch(() => {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    }
  };

  const handleClick = (notif) => {
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await api.deleteNotification(id).catch(() => {});
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (!user) return null;

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        className="btn btn-secondary"
        style={{ position: 'relative', padding: '6px 8px' }}
        onClick={handleOpen}
      >
        <BellSprite />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            background: '#eb0400', color: '#fff',
            fontFamily: 'var(--font-pixel)',
            fontSize: '0.4rem', fontWeight: 800,
            borderRadius: '2px', minWidth: 14, height: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, padding: '0 2px',
            boxShadow: '1px 1px 0 rgba(0,0,0,0.4)',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '44px',
          width: '320px', background: '#18181b',
          border: '1px solid #2a2a2e', borderRadius: '2px',
          boxShadow: '3px 3px 0 rgba(0,0,0,0.5)',
          zIndex: 999, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #2a2a2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.6rem', fontWeight: 700 }}>Notifications</span>
            {notifications.length > 0 && (
              <button style={{ background: 'none', border: 'none', color: '#adadb8', fontSize: '0.8rem', cursor: 'pointer' }}
                onClick={() => { api.markRead(); setUnread(0); setNotifications(prev => prev.map(n => ({ ...n, is_read: true }))); }}>
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#adadb8', fontSize: '0.9rem' }}>
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    padding: '12px 16px', cursor: n.link ? 'pointer' : 'default',
                    background: n.is_read ? 'transparent' : '#D6E9AA11',
                    borderBottom: '1px solid #1a1a1e',
                    display: 'flex', gap: '10px', alignItems: 'flex-start',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#2a2a2e'}
                  onMouseLeave={e => e.currentTarget.style.background = n.is_read ? 'transparent' : '#D6E9AA11'}
                >
                  <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.5rem', flexShrink: 0, color: n.type === 'live' ? '#eb0400' : n.type === 'tip' ? '#D6E9AA' : n.type === 'sub' ? '#ffd700' : '#adadb8' }}>{TYPE_ICON[n.type] || TYPE_ICON.default}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: '0.8rem', color: '#adadb8', marginTop: '2px' }}>{n.body}</div>}
                    <div style={{ fontSize: '0.75rem', color: '#adadb8', marginTop: '4px' }}>
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={e => handleDelete(e, n.id)}
                    style={{ background: 'none', border: 'none', color: '#adadb8', cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0, padding: '0 2px' }}
                  >✕</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
